import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TeamsService } from 'src/app/providers/services/teams.service';
import { Market } from 'src/app/storage/models/market.model';
import { MobileDevice } from 'src/app/storage/models/mobile-device.model';
import { SelectOption } from '@df/design-system-components';
import { PeopleLocation } from 'src/app/storage/models/people-location.model';
import { Team } from 'src/app/storage/models/team.model';

@Component({
    selector: 'app-create-team',
    templateUrl: './create-team.component.html',
    styleUrls: ['./create-team.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateTeamComponent implements OnInit {
  form: UntypedFormGroup;

  peopleLocations: PeopleLocation[];
  mobileDevices: MobileDevice[];
  mobileDeviceOptions: SelectOption<number>[] = [];

  @Input() market: Market;
  @Output() closeModal = new EventEmitter<any>();

  constructor(
    private teamsService: TeamsService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.form = new UntypedFormGroup({
      teamMemberId: new UntypedFormControl('', [Validators.required]),
      teamMemberFirstName: new UntypedFormControl('', [Validators.required]),
      teamMemberName: new UntypedFormControl('', [Validators.required]),
      teamMemberLanguage: new UntypedFormControl('', [this.languageValidator()]),
      teamMemberCellularPhone: new UntypedFormControl('', []),
      teamMemberEmail: new UntypedFormControl('', []),
      peoplelocationcode: new UntypedFormControl('', [Validators.required]),
      mobileDevicepk: new UntypedFormControl(null, []),
      external: new UntypedFormControl(false, []) 
    });

    this.form.get('external').valueChanges.subscribe((isExternal: boolean) => {
      const idControl = this.form.get('teamMemberId');
      const emailControl = this.form.get('teamMemberEmail');

      if (isExternal) {
        idControl.reset(null);
        idControl.clearValidators();
        idControl.disable();

        emailControl.setValidators([Validators.required, this.externalEmailValidator()]);
        emailControl.enable();
      } else {
        idControl.setValidators([Validators.required]);
        idControl.enable();
        idControl.reset('');

        emailControl.clearValidators();
      }

      idControl.updateValueAndValidity();
      emailControl.updateValueAndValidity();
    });

    this.peopleLocations = await this.teamsService.getMarketPeopleLocations(this.market.marketname);
    this.mobileDevices = await this.teamsService.getMobileDevices();
    this.mobileDeviceOptions = (this.mobileDevices ?? []).map(mobileDevice => ({
      value: mobileDevice.mobileDevicepk,
      label: `${mobileDevice.mobileDeviceModelSupplier} / ${mobileDevice.mobileDeviceName}`
    }));

    if (this.peopleLocations == null || this.mobileDevices == null) { this.close(); }
  }

  close(): void {
    this.closeModal.emit();
  }

  private languageValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const value = control.value;
      if (value.length > 2) { return {wrongFormat: {value: control.value}}; }
      return null;
    }
  }

  async create(): Promise<void> {
    const controls = this.form.controls;

    const peoplelocationcode = controls['peoplelocationcode'].value;
    const peoplelocationname = this.peopleLocations.find(peopleLocation => peopleLocation.peoplelocationcode === peoplelocationcode).peoplelocationname;

    const mobileDevicepk = controls['mobileDevicepk'].value;
    const mobileDevice = mobileDevicepk != null ?
                        this.mobileDevices.find(mobileDevice => mobileDevice.mobileDevicepk == mobileDevicepk) :
                        null;

    const team = new Team({
      teampk: null,
      teamMemberId: controls['external'].value ? null : controls['teamMemberId'].value,
      teamMemberFirstName: controls['teamMemberFirstName'].value,
      teamMemberName: controls['teamMemberName'].value,
      teamMemberLanguage: controls['teamMemberLanguage'].value,
      teamMemberCellularPhone: controls['teamMemberCellularPhone'].value,
      teamMemberEmail: controls['teamMemberEmail'].value,
      peoplelocationcode: peoplelocationcode,
      peoplelocationname: peoplelocationname,
      mobileDevicepk: mobileDevice?.mobileDevicepk,
      mobileDeviceLTEProvider: mobileDevice?.mobileDeviceLTEProvider,
      mobileDeviceSimNumber: mobileDevice?.mobileDeviceSimNumber,
      deletionFlag: null,
      roles: []
    });

    const isTeamAdded = await this.teamsService.addTeam(this.market.marketname, team);

    if (isTeamAdded) { this.closeModal.emit(team); }
  }

  private externalEmailValidator(): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    const value = control.value || '';
    if (value.toLowerCase().endsWith('@arkema.com')) {
      return { forbiddenDomain: { value: control.value } };
    }
    return null;
  };
}


}
